import { useState } from 'react';
import Seo from '@/components/Seo';
import Turnstile from '@/components/Turnstile';
import { SITE } from '@/lib/site';
import { getSeo } from '@/lib/seo';
import { LIMITS, validateContact, type ContactInput } from '@/lib/validation';
import { trackConversion, CONTACT_FORM_CONVERSION } from '@/lib/analytics';

const BUDGET_OPTIONS = [
  'Under £250',
  '£250 – £500',
  '£500 – £1,000',
  '£1,000 – £2,500',
  '£2,500 – £5,000',
  '£5,000+',
  'Not sure yet',
];

type Errors = Partial<Record<keyof ContactInput | 'form', string>>;

export default function Contact() {
  const [form, setForm] = useState<ContactInput>({
    name: '',
    email: '',
    phone: '',
    description: '',
    budget: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  // null = challenge present but not yet solved.
  // ''   = no Turnstile configured (dev) → submit proceeds.
  // string = solved token.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const update = <K extends keyof ContactInput>(key: K, value: ContactInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = validateContact(form);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    if (turnstileToken === null) {
      setErrors({ form: 'Please complete the challenge above before sending.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...result.data, turnstileToken }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          errors?: Errors;
          error?: string;
        };
        if (body.errors) {
          setErrors(body.errors);
        } else {
          setErrors({ form: body.error ?? "Something went wrong. Please try again or email us directly." });
        }
        return;
      }
      setSent(true);
      trackConversion(
        CONTACT_FORM_CONVERSION.label,
        CONTACT_FORM_CONVERSION.value,
        CONTACT_FORM_CONVERSION.currency
      );
    } catch {
      setErrors({ form: 'Network error. Please try again or email us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        path="/contact"
        fullTitle={getSeo('contact').title}
        description={getSeo('contact').description}
        ogImage={getSeo('contact').og_image ?? undefined}
      />
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-page gap-12 px-5 py-20 sm:px-8 sm:py-24 md:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="text-sm font-medium uppercase tracking-wider text-accent">
              Contact
            </p>
            <h1 className="mt-3 font-display text-4xl leading-tight sm:text-5xl">
              Tell us about it.
            </h1>
            <p className="mt-5 text-fg-muted">
              Send us a description about what you need and your rough budget,
              and we'll come back with a fixed quote within a working day.
            </p>

            <dl className="mt-10 space-y-5 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-fg-subtle">
                  Email
                </dt>
                <dd className="mt-1">
                  <a className="link" href={`mailto:${SITE.email}`}>
                    {SITE.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-fg-subtle">
                  Phone
                </dt>
                <dd className="mt-1">
                  <a className="link" href={SITE.phoneHref}>
                    {SITE.phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-fg-subtle">
                  Hours
                </dt>
                <dd className="mt-1 text-fg-muted">
                  Mon–Fri, 9am–6pm UK time. We reply within 1 working day.
                </dd>
              </div>
            </dl>
          </div>

          <div>
            {sent ? (
              <div className="card p-8">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/15 text-accent">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 12.5l5 5L20 7"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="mt-5 text-2xl">Thanks — message sent.</h2>
                <p className="mt-3 text-fg-muted">
                  We've received your enquiry and you'll hear back from us
                  within a working day. If it's urgent, give us a call on{' '}
                  <a className="link" href={SITE.phoneHref}>
                    {SITE.phone}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="card p-6 sm:p-8">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Your name"
                    id="name"
                    error={errors.name}
                    required
                  >
                    <input
                      id="name"
                      type="text"
                      autoComplete="name"
                      maxLength={LIMITS.name.max}
                      className="input"
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                    />
                  </Field>
                  <Field label="Email" id="email" error={errors.email} required>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      maxLength={LIMITS.email.max}
                      className="input"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Phone"
                    id="phone"
                    error={errors.phone}
                    optional
                  >
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      maxLength={LIMITS.phone.max}
                      className="input"
                      value={form.phone ?? ''}
                      onChange={(e) => update('phone', e.target.value)}
                    />
                  </Field>
                  <Field label="Budget" id="budget" error={errors.budget} required>
                    <select
                      id="budget"
                      className="input"
                      value={form.budget}
                      onChange={(e) => update('budget', e.target.value)}
                    >
                      <option value="">Select a range…</option>
                      {BUDGET_OPTIONS.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field
                    label="What are you after?"
                    id="description"
                    error={errors.description}
                    required
                    hint={`${form.description.length} / ${LIMITS.description.max}`}
                  >
                    <textarea
                      id="description"
                      rows={6}
                      maxLength={LIMITS.description.max}
                      className="input resize-y"
                      placeholder="A few lines about the site you'd like, who it's for, and any deadlines."
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                    />
                  </Field>
                </div>

                {/* Honeypot — hidden from users, picked up by bots. */}
                <div
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
                >
                  {/* Honeypot: a real user never fills this. Field name is
                      deliberately something password managers / browser
                      autofill won't recognise. */}
                  <label htmlFor="yd_extra_field">Leave blank</label>
                  <input
                    id="yd_extra_field"
                    name="yd_extra_field"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.honeypot ?? ''}
                    onChange={(e) => update('honeypot', e.target.value)}
                  />
                </div>

                <Turnstile onToken={setTurnstileToken} />

                {errors.form && (
                  <p className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                    {errors.form}
                  </p>
                )}

                <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-fg-subtle">
                    We'll only use your details to reply to your enquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full sm:w-auto disabled:opacity-60"
                  >
                    {submitting ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  id,
  error,
  required,
  optional,
  hint,
  children,
}: {
  label: string;
  id: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="label">
          {label}
          {optional && <span className="ml-1 text-fg-subtle">(optional)</span>}
          {required && <span className="ml-1 text-accent">*</span>}
        </label>
        {hint && <span className="text-xs text-fg-subtle">{hint}</span>}
      </div>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
